import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import DividerMotif from "../../components/Brand/DividerMotif";
import Footer from "../../components/Layout/Footer";
import { PublicHeader } from "../../components/Layout/PublicHeader";
import { useAuth } from "../../contexts/AuthContext";
import { getJob } from "../../lib/jobsApi";
import type { PublicJobDetail, PublicTask, SubmissionType } from "../../lib/jobsApi";
import { ApiError } from "../../lib/api";
import { formatRelativeDate } from "../../lib/formatRelativeDate";
import { useDocumentMeta } from "../../lib/useDocumentMeta";

/*
 * Velaigal /jobs/:id — the public job detail page.
 *
 * Fully public, same header/footer shell as JobsList and the rest of the
 * marketing site. The Apply CTA changes by viewer:
 *   - signed out          -> /login?returnTo=/jobs/:id (comes back here after sign-in)
 *   - signed-in job seeker -> /jobs/:id/apply (the seeker-gated apply flow)
 *   - signed-in HR/admin   -> no Apply button at all
 * A closed job never shows an Apply button, regardless of viewer.
 */

const SUBMISSION_TYPE_LABELS: Record<SubmissionType, string> = {
  hosted_link: "Hosted link",
  repo: "Repository",
  video: "Video",
  document: "Document",
};

const TaskCard: React.FC<{ task: PublicTask; index: number }> = ({ task, index }) => (
  <article className="card">
    <span className="label-editorial" style={{ color: "var(--color-saffron)" }}>
      Task {String(index + 1).padStart(2, "0")}
    </span>
    <h3 className="font-display text-xl font-medium text-ink leading-snug mt-2">{task.title}</h3>
    <p className="mt-3 text-sm text-ink-secondary leading-relaxed whitespace-pre-line">{task.brief}</p>
    <div className="mt-4 flex flex-wrap gap-2">
      {task.allowedSubmissionTypes.map((type) => (
        <span key={type} className="chip">
          {SUBMISSION_TYPE_LABELS[type]}
        </span>
      ))}
    </div>
  </article>
);

const LoadingBlock: React.FC = () => (
  <div className="max-w-4xl mx-auto px-8 py-24 text-center">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-6 text-sm text-ink-tertiary">Loading this job.</p>
  </div>
);

const NotFoundBlock: React.FC = () => (
  <div className="max-w-4xl mx-auto px-8 py-24 text-center">
    <span className="label-editorial block">Not found</span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mt-4">
      This job isn&apos;t available.
    </h1>
    <span aria-hidden="true" className="block w-12 h-px bg-saffron mx-auto mt-6" />
    <p className="mt-6 text-base text-ink-secondary leading-relaxed max-w-md mx-auto">
      It may have been unpublished, or the link is out of date.
    </p>
    <div className="mt-10">
      <Link to="/jobs" className="btn-primary">
        Browse open jobs
      </Link>
    </div>
  </div>
);

const ErrorBlock: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="max-w-4xl mx-auto px-8 py-24 text-center">
    <span className="label-editorial" style={{ color: "var(--color-error)" }}>
      Something went wrong
    </span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mt-4">
      Couldn&apos;t load this job.
    </h1>
    <p className="mt-6 text-base text-ink-secondary leading-relaxed max-w-md mx-auto">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary mt-8">
      Try again
    </button>
  </div>
);

const JobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [data, setData] = useState<PublicJobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; notFound: boolean } | null>(null);

  useDocumentMeta(
    data ? `${data.job.title} · Velaigal` : "Velaigal — Hiring built on what people can actually do.",
    data ? `${data.job.title} at ${data.job.companyName} — apply on Velaigal with real tasks instead of trick questions.` : undefined
  );

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getJob(id);
      setData(res);
    } catch (err) {
      if (err instanceof ApiError) {
        setError({ message: err.message, notFound: err.status === 404 });
      } else {
        setError({ message: "Check your connection and try again.", notFound: false });
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  let body: React.ReactNode;
  if (loading) {
    body = <LoadingBlock />;
  } else if (error?.notFound) {
    body = <NotFoundBlock />;
  } else if (error) {
    body = <ErrorBlock message={error.message} onRetry={() => void load()} />;
  } else if (data) {
    const { job, tasks } = data;
    const isClosed = job.status === "closed";

    let applySection: React.ReactNode = null;
    if (!isClosed) {
      if (!user) {
        const returnTo = encodeURIComponent(`/jobs/${job.id}`);
        applySection = (
          <Link to={`/login?returnTo=${returnTo}`} className="btn-primary no-underline" style={{ textDecoration: "none" }}>
            Apply for this job
          </Link>
        );
      } else if (user.userType === "jobseeker") {
        applySection = (
          <Link to={`/jobs/${job.id}/apply`} className="btn-primary no-underline" style={{ textDecoration: "none" }}>
            Apply for this job
          </Link>
        );
      } else {
        applySection = (
          <p className="text-sm text-ink-tertiary">
            Signed in as {user.userType === "hr" ? "a recruiter" : "an admin"} — applications are open to job seekers only.
          </p>
        );
      }
    }

    body = (
      <>
        <section className="relative overflow-hidden">
          <div className="relative z-10 max-w-4xl mx-auto px-8 pt-16 pb-12">
            <Link
              to="/jobs"
              className="velaigal-link text-sm font-medium inline-flex items-center gap-1.5"
              style={{ textDecoration: "none" }}
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
              Back to jobs
            </Link>

            <span className="label-editorial block mt-8" style={{ color: "var(--color-saffron)" }}>
              {job.roleFamily}
            </span>
            <h1
              className="font-display text-4xl md:text-5xl font-medium text-ink leading-tight tracking-tight mt-4"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
            >
              {job.title}
            </h1>
            <p className="mt-3 text-lg text-ink-secondary">{job.companyName}</p>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ink-tertiary">
              {job.positionLevel && <span>{job.positionLevel}</span>}
              <span>{job.experienceYearsMin}+ yrs experience</span>
              {job.education && <span>{job.education}</span>}
              {job.salaryDisplay && <span className="text-ink font-medium">{job.salaryDisplay}</span>}
              <span>Posted {formatRelativeDate(job.publishedAt)}</span>
            </div>

            {isClosed && (
              <div
                role="status"
                className="mt-8 inline-flex items-center gap-2.5 px-4 py-3 border border-warning bg-paper text-sm text-ink rounded"
              >
                <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-warning flex-shrink-0" />
                This job has closed. The team is no longer accepting applications.
              </div>
            )}

            {applySection && <div className="mt-8">{applySection}</div>}
          </div>
        </section>

        <DividerMotif className="py-2" />

        <section>
          <div className="max-w-4xl mx-auto px-8 py-16">
            <span className="label-editorial">About the role</span>
            <div className="mt-6 text-base text-ink-secondary leading-relaxed whitespace-pre-line max-w-3xl">
              {job.description}
            </div>
          </div>
        </section>

        {tasks.length > 0 && (
          <>
            <DividerMotif className="py-2" />
            <section>
              <div className="max-w-4xl mx-auto px-8 py-16">
                <span className="label-editorial">Tasks you&apos;ll complete</span>
                <h2 className="font-display text-2xl md:text-3xl font-medium text-ink leading-snug mt-4 max-w-2xl">
                  Show your work, not your resume.
                </h2>
                <div className="mt-10 space-y-6">
                  {tasks.map((task, idx) => (
                    <TaskCard key={task.id} task={task} index={idx} />
                  ))}
                </div>
              </div>
            </section>
          </>
        )}
      </>
    );
  } else {
    body = null;
  }

  return (
    <div className="min-h-screen w-full bg-cream text-ink">
      <PublicHeader />
      {body}
      <Footer />
    </div>
  );
};

export default JobDetail;
